"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shows an email's HTML as it looks in Gmail.
 *
 * The HTML is sanitised when stored (lib/inbox/email-html.ts); the frame is
 * the second line of defence. Its sandbox has no allow-scripts, so nothing in
 * the email can run, and its styles cannot leak into the app. allow-same-
 * origin is safe without scripts and lets the frame be sized to its content.
 */

const FRAME_STYLES = `
  html, body { margin: 0; padding: 0; }
  body {
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #111827;
    overflow-wrap: anywhere;
  }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  pre { white-space: pre-wrap; }
  details.email-quote { margin-top: 8px; }
  details.email-quote > summary {
    display: inline-block;
    list-style: none;
    cursor: pointer;
    padding: 0 6px;
    border-radius: 4px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    line-height: 16px;
  }
  details.email-quote > summary::-webkit-details-marker { display: none; }
  details.email-quote[open] > summary { margin-bottom: 8px; }
  details.email-quote[open] {
    padding-left: 10px;
    border-left: 2px solid #e5e7eb;
    color: #4b5563;
  }
`;

const toDocument = (html: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${FRAME_STYLES}</style></head><body>${html}</body></html>`;

export function EmailHtmlFrame({ html }: { html: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  const measure = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) setHeight(doc.documentElement.scrollHeight);
  }, []);

  // Images loading late, a quote unfolding and the pane resizing all change
  // the content's height.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    let observer: ResizeObserver | null = null;
    const onLoad = () => {
      measure();
      const body = frame.contentDocument?.body;
      if (!body) return;
      observer?.disconnect();
      observer = new ResizeObserver(measure);
      observer.observe(body);
    };

    frame.addEventListener("load", onLoad);
    if (frame.contentDocument?.readyState === "complete") onLoad();
    return () => {
      frame.removeEventListener("load", onLoad);
      observer?.disconnect();
    };
  }, [html, measure]);

  return (
    <iframe
      ref={frameRef}
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={toDocument(html)}
      className="block w-full border-0"
      style={{ height: height || 80 }}
    />
  );
}
